import { get } from 'lodash';
import { PdfVisibilityDirectiveKind } from '../schema/directives/pdf-visibility/IPdfVisibilityDirective';
import { extractPdfVisibility } from '../schema/directives/pdf-visibility/extractPdfVisibility';
import { createMaterializedTree } from '../schema/expressions/references/resolve/createMaterializedTree';
import { IEngineSection } from '../schema/sections/IEngineSection';
import { Widget } from '../schema/widgets/Widget';
import { WidgetRepository } from '../schema/widgets/repository/WidgetRepository';
import { hasChildren } from '../schema/widgets/repository/containers/hasChildren';
import { getWidgetPath } from '../validator/form-builder/getWidgetPath';
import { HydratedWidget, IHydrationContext } from './IHydrationContext';
import { hydrateBooleanCondition } from './expression/hydrateBooleanCondition';
import { hydrateRichTexts } from './rte/hydrateRichTexts';

export function hydrateEngineSection(
  context: IHydrationContext,
  section: IEngineSection,
) {
  const visible = hydrateBooleanCondition(
    context,
    'root',
    section.ultimaVersione?.visibile,
  );
  const enabled = hydrateBooleanCondition(
    context,
    'root',
    section.ultimaVersione?.compilabile,
  );

  if (!visible || !enabled || !section.ultimaVersione?.elementi) return null;

  const materializedRoot = createMaterializedTree(
    section.ultimaVersione?.elementi,
  );

  // Ricostruisco il contesto sostituendo i valori con quelli della sezione
  // attualmente presa in considerazione. Al momento non sono previste
  // gestioni extra-sezione, quindi non è necessario gestire il caso in cui
  // ci siano riferimenti all'esterno. In questo modo, tutti i valori
  // sono root e la logica di hydrateEngineWidget resta invariata.
  const newContext: IHydrationContext = {
    ...context,
    values: context.values?.[section.id] ?? {},
  };

  return {
    ...section,
    ultimaVersione: {
      ...section.ultimaVersione,
      elementi: {
        ...materializedRoot,
        children: hydrateEngineWidgets(
          newContext,
          materializedRoot.children ?? [],
        ),
      },
    },
  };
}

export function hydrateEngineWidgets(
  context: IHydrationContext,
  widgets: Widget[],
): HydratedWidget[] {
  return widgets
    .map(widget => hydrateEngineWidget(context, widget))
    .filter(Boolean) as HydratedWidget[];
}

export function hydrateEngineWidget(
  context: IHydrationContext,
  widget: Widget,
): HydratedWidget | null {
  const widgetType = WidgetRepository.find(widget.type);
  if (!widgetType) return null;

  if (widget.layout && 'hide' in widget.layout && widget.layout.hide) {
    return null;
  }

  // Recupero la configurazione di visibilità della direttiva PdfVisibility
  const pdfVisibility = extractPdfVisibility(widget);

  // Se la visibilità è indicata unicamente per la compilazione, non "renderizzo"
  // il widget (non viene idratato)
  if (pdfVisibility?.visibility === PdfVisibilityDirectiveKind.COMPILATION) {
    return null;
  }

  const widgetFormName = getWidgetPath(widget, context.prefix);
  const value = get(context.values, widgetFormName);

  const visible = hydrateBooleanCondition(
    context,
    widgetFormName,
    widget.visible,
  );

  if (!visible) return null;

  // Facciamo side effect sul widget
  hydrateRichTexts(context, widgetType, widget, widgetFormName);

  if (!widgetType.hydrate && hasChildren(widget)) {
    return {
      ...widget,
      widgetFormName,
      ...({
        children: hydrateEngineWidgets(context, widget.children),
      } as any),
    };
  }

  const hydrated = widgetType.hydrate?.(
    { context, widget, path: widgetFormName },
    hydrateEngineWidgets,
  );

  return {
    ...widget,
    widgetFormName,
    value,
    ...(hydrated as any),
  };
}
